/**
 * Centro de sincronización: estado de cada operación, causa y reintentos permitidos.
 *
 * Al cajero se le muestra el estado y un mensaje comprensible. Nunca se le enseña un
 * payload, un código interno ni una traza.
 */

import { useCallback, useEffect, useState } from 'react';
import { formatInstant, OUTBOX_STATUS_LABELS, type OutboxStatus } from '@senm/domain';
import type { LocalOutboxEntry } from '../local/db.js';
import type { StoredSession } from '../local/session.js';
import { lastSyncedAt, listOutbox, retryEntry } from '../sync/engine.js';

const TONO: Record<OutboxStatus, string> = {
  PENDIENTE: 'aviso',
  PROCESANDO: '',
  SINCRONIZADO: 'ok',
  RECHAZADO: 'error',
  CONFLICTO: 'error',
};

const SIMBOLO: Record<OutboxStatus, string> = {
  PENDIENTE: '◷',
  PROCESANDO: '↻',
  SINCRONIZADO: '✓',
  RECHAZADO: '✕',
  CONFLICTO: '!',
};

export function SyncCenterPage(props: {
  session: StoredSession;
  onSincronizar: () => Promise<void>;
  sincronizando: boolean;
}): JSX.Element {
  const [entradas, setEntradas] = useState<LocalOutboxEntry[]>([]);
  const [ultima, setUltima] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    setEntradas(await listOutbox());
    setUltima(await lastSyncedAt());
  }, []);

  useEffect(() => {
    void recargar();
  }, [recargar, props.sincronizando]);

  const porEstado = (estado: OutboxStatus): LocalOutboxEntry[] =>
    entradas.filter((entrada) => entrada.status === estado);

  return (
    <>
      <h1>Centro de sincronización</h1>
      <p className="ayuda">
        {ultima === null
          ? 'Este dispositivo nunca ha sincronizado.'
          : `Última sincronización: ${formatInstant(ultima, props.session.user.timeZone)}.`}
      </p>

      <div className="pila">
        <button type="button" className="boton primario" onClick={() => void props.onSincronizar()} disabled={props.sincronizando}>
          {props.sincronizando ? 'Sincronizando…' : 'Sincronizar ahora'}
        </button>
        <button type="button" className="boton" onClick={() => void recargar()}>
          Actualizar lista
        </button>
      </div>

      <div className="rejilla" style={{ marginTop: 16 }}>
        {(['PENDIENTE', 'CONFLICTO', 'RECHAZADO', 'SINCRONIZADO'] as OutboxStatus[]).map((estado) => (
          <div className="tarjeta dato" key={estado}>
            <span className="etiqueta">{OUTBOX_STATUS_LABELS[estado]}</span>
            <span className="valor">{porEstado(estado).length}</span>
          </div>
        ))}
      </div>

      <h2>Operaciones</h2>
      {entradas.length === 0 ? (
        <p className="vacio">No hay operaciones en la cola.</p>
      ) : (
        <div className="tarjeta tabla-desplazable">
          <table>
            <thead>
              <tr>
                <th scope="col">Capturada</th>
                <th scope="col">Tipo</th>
                <th scope="col">Estado</th>
                <th scope="col">Detalle</th>
                <th scope="col" className="numero">Intentos</th>
                <th scope="col">
                  <span className="saltar">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {entradas.map((entrada) => (
                <tr key={entrada.id}>
                  <td>{formatInstant(entrada.createdAt, props.session.user.timeZone)}</td>
                  <td>{entrada.type === 'PAGO' ? 'Pago' : entrada.type === 'ANULACION' ? 'Anulación' : 'Reimpresión'}</td>
                  <td>
                    <span className={`marca-estado ${TONO[entrada.status]}`}>
                      {SIMBOLO[entrada.status]} {OUTBOX_STATUS_LABELS[entrada.status]}
                    </span>
                  </td>
                  <td>
                    {/* Mensaje en lenguaje natural; nunca el payload ni el código interno. */}
                    {entrada.lastErrorMessage ??
                      (entrada.status === 'PENDIENTE'
                        ? 'Se enviará automáticamente cuando haya conexión.'
                        : entrada.status === 'SINCRONIZADO'
                          ? 'Confirmada por el servidor.'
                          : '')}
                  </td>
                  <td className="numero">{entrada.attempts}</td>
                  <td>
                    {/* Solo se puede reintentar tras una corrección: los estados terminales
                        no se reintentan solos, y el usuario debe pedirlo explícitamente. */}
                    {(entrada.status === 'RECHAZADO' || entrada.status === 'CONFLICTO') && (
                      <button
                        type="button"
                        className="boton"
                        onClick={async () => {
                          await retryEntry(entrada.id, new Date().toISOString());
                          await recargar();
                        }}
                      >
                        Reintentar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
